/**
 * Hook for detecting listening TCP ports inside a sandbox.
 * Opens a WebSocket exec stream that runs `ss -plunt` in a loop every 5 seconds
 * and parses the output to detect open ports.
 *
 * Uses the same proven pattern as useProcessList: launch /bin/bash with TTY,
 * then send the collection script via stdin after connection is established.
 *
 * @module hooks/usePortDetector
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useExecStream, type ConnectionState } from '@/hooks';

export interface DetectedPort {
  port: number;
  protocol: string;
  process: string;
  address: string;
}

export interface UsePortDetectorReturn {
  detectedPorts: DetectedPort[];
  error: string | null;
  isPolling: boolean;
}

/**
 * Well-known system/infrastructure ports to ignore.
 * These are commonly used by system services and not user applications.
 */
const SYSTEM_PORTS = new Set([22, 53, 111, 631, 5355]);

/**
 * Shell script that collects listening port data in a loop.
 * Sent via stdin to a /bin/bash session, same pattern as useProcessList.
 *
 * The output is wrapped in markers so the client can reliably extract
 * each snapshot of `ss` output from the streaming data.
 */
const PORT_SCAN_SCRIPT = `
while true; do
  echo "---PORTS_START---"
  ss -plunt state listening 2>/dev/null || netstat -tlnp 2>/dev/null
  echo "---PORTS_END---"
  sleep 5
done
`;

/**
 * Parse the stdout of `ss -plunt state listening` into DetectedPort[].
 *
 * When called with a state filter, `ss` omits the State column:
 *   Netid  Recv-Q  Send-Q  Local Address:Port  Peer Address:Port  Process
 *   tcp    0       5       0.0.0.0:8080        0.0.0.0:*          users:(("node",pid=123,fd=4))
 *
 * Without a state filter the State column is present:
 *   Netid  State   Recv-Q  Send-Q  Local Address:Port  Peer Address:Port  Process
 *   tcp    LISTEN  0       128     0.0.0.0:8080        0.0.0.0:*          users:(("node",pid=123,fd=4))
 *
 * We detect the column layout from the header to handle both cases.
 */
function parseSsOutput(stdout: string): DetectedPort[] {
  const lines = stdout.split('\n').filter((l) => l.trim().length > 0);
  const ports: DetectedPort[] = [];
  const seen = new Set<number>();

  // Determine local address column index from the header
  let localAddrCol = 3; // default: state-filtered output (no State column)
  if (lines.length > 0) {
    const headerCols = lines[0].trim().split(/\s+/);
    // If header contains 'State', the columns are shifted right by 1
    if (headerCols.includes('State')) {
      localAddrCol = 4;
    }
  }

  for (let i = 1; i < lines.length; i++) {
    const line = lines[i];
    const cols = line.trim().split(/\s+/);
    if (cols.length <= localAddrCol) continue;

    const localAddr = cols[localAddrCol];
    if (!localAddr) continue;

    // Extract port: last segment after the last ':'
    const lastColon = localAddr.lastIndexOf(':');
    if (lastColon === -1) continue;

    const portStr = localAddr.substring(lastColon + 1);
    const portNum = parseInt(portStr, 10);
    if (isNaN(portNum) || portNum <= 0) continue;

    // Filter system ports and deduplicate
    if (SYSTEM_PORTS.has(portNum) || seen.has(portNum)) continue;
    seen.add(portNum);

    const address = localAddr.substring(0, lastColon);

    // Extract protocol from first column
    const protocol = cols[0] || 'tcp';

    // Extract process name from the users:(("name",...)) field
    let process = '-';
    const procMatch = line.match(/users:\(\("([^"]+)"/);
    if (procMatch) {
      process = procMatch[1];
    }

    ports.push({ port: portNum, protocol, process, address });
  }

  return ports;
}

/**
 * Detects listening TCP ports inside a sandbox using a WebSocket exec stream.
 * Opens a /bin/bash session and runs `ss -plunt` in a loop every 5 seconds.
 *
 * @param sandboxId - The sandbox to scan
 * @param enabled - Whether detection is active. When false, the connection is
 *   closed and ports are cleared.
 */
export function usePortDetector(sandboxId: string, enabled: boolean): UsePortDetectorReturn {
  const [detectedPorts, setDetectedPorts] = useState<DetectedPort[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [commandSent, setCommandSent] = useState(false);

  const outputBufferRef = useRef<string>('');
  const disconnectRef = useRef<(() => void) | null>(null);
  const connectRef = useRef<((height?: number, width?: number) => void) | null>(null);
  const sendInputRef = useRef<((data: string) => void) | null>(null);
  const hasConnectedRef = useRef(false);

  const handleOutput = useCallback((data: string) => {
    outputBufferRef.current += data;

    // Prevent unbounded buffer growth
    if (outputBufferRef.current.length > 200000) {
      outputBufferRef.current = outputBufferRef.current.slice(-100000);
    }

    const buffer = outputBufferRef.current;
    const startIdx = buffer.indexOf('---PORTS_START---');
    const endIdx = buffer.indexOf('---PORTS_END---');

    if (startIdx !== -1 && endIdx !== -1 && endIdx > startIdx) {
      const ssOutput = buffer.substring(startIdx + 17, endIdx).trim();
      try {
        const parsed = parseSsOutput(ssOutput);
        setDetectedPorts(parsed);
        setError(null);
      } catch {
        // Parse errors during partial reads; silently ignore
      }
    }

    // Clear buffer past the end marker to prepare for next snapshot
    if (endIdx !== -1) {
      outputBufferRef.current = buffer.substring(endIdx + 15);
    }
  }, []);

  const handleExit = useCallback((exitCode: number) => {
    if (exitCode !== 0) {
      setError('Port scanner exited unexpectedly');
    }
  }, []);

  const handleError = useCallback((err: string) => {
    setError(`Connection error: ${err}`);
  }, []);

  const handleStateChange = useCallback((state: ConnectionState) => {
    setIsPolling(state === 'connected');
    if (state === 'connected') {
      setError(null);
    }
  }, []);

  const execStream = useExecStream({
    sandboxId,
    command: '/bin/bash',
    environment: { HISTFILE: '/dev/null' },
    onOutput: handleOutput,
    onExit: handleExit,
    onError: handleError,
    onStateChange: handleStateChange,
    autoReconnect: true,
  });

  // Keep refs updated so callbacks always use the latest function references
  useEffect(() => {
    disconnectRef.current = execStream.disconnect;
    connectRef.current = execStream.connect;
    sendInputRef.current = execStream.sendInput;
  }, [execStream.disconnect, execStream.connect, execStream.sendInput]);

  // Send port scan script when connected (same pattern as useProcessList)
  useEffect(() => {
    if (isPolling && !commandSent) {
      const timer = setTimeout(() => {
        sendInputRef.current?.(PORT_SCAN_SCRIPT + '\n');
        setCommandSent(true);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isPolling, commandSent]);

  // Auto-connect when enabled, disconnect when disabled
  useEffect(() => {
    if (enabled) {
      if (!hasConnectedRef.current && connectRef.current) {
        hasConnectedRef.current = true;
        outputBufferRef.current = '';
        setCommandSent(false);
        connectRef.current(24, 120);
      }
    } else {
      hasConnectedRef.current = false;
      outputBufferRef.current = '';
      setCommandSent(false);
      disconnectRef.current?.();
      setDetectedPorts([]);
      setError(null);
      setIsPolling(false);
    }
  }, [enabled]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnectRef.current?.();
    };
  }, []);

  return { detectedPorts, error, isPolling };
}
